/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import { useState } from "react";

import cn from "classnames";

import RoomPlaceholder from "assets/tripdetails/RoomPlaceholder.png";
import RoomUserPlaceholder from "assets/tripdetails/RoomUserPlaceholder.png";
import { UnGuard } from "constants/interfaces";
import { GetTripsResponse } from "lib/tripsApiTypes";
import ROUTES from "routes";
import getFormattedDate from "utils/getFormattedDate";

import {
  Button,
  HR,
  IMG,
  Stack,
  Text,
  Avatar,
  Rating,
  FontAwesomeIcon,
} from "components";
import { useTranslation } from "react-i18next";
import { pushToDataLayer } from "utils/gtm";

const StaySection = ({
  reservations,
  userId,
  tripId,
}: {
  reservations: UnGuard<
    GetTripsResponse["approved_trips"]
  >[number]["reservations"];
  userId: string;
  tripId?: string | number;
}) => {
  const { t } = useTranslation();
  const [isExpanded, setExpanded] = useState(() => {
    return Object.keys(reservations ?? {}).reduce<Record<string, boolean>>(
      (acc, day) => {
        acc[day] = true;
        return acc;
      },
      {},
    );
  });

  if (!reservations?.length) return null;

  return (
    <Stack className="gap-y-5">
      {reservations?.map((reservation, index) => (
        <Stack className="gap-y-10" key={index}>
          <div className="flex items-center -bg--theme-colors-surface-neutral-level03 justify-between -rounded-heading-container">
            <Button
              variant={"ghost-unstyled"}
              className="p-0"
              onClick={() => {
                pushToDataLayer({
                  event: isExpanded[index]
                    ? "my_reservations_collapse"
                    : "my_reservations_expand",
                  screen_name: "dashboard",
                  journey_step: "reservations",
                });
                setExpanded((prev) => ({
                  ...prev,
                  [index]: !prev[index],
                }));
              }}
            >
              <div className="flex gap-x-1.5 -text--theme-colors-action-ghost-default-fg items-center">
                <div className="p-2">
                  <FontAwesomeIcon
                    className={cn(
                      isExpanded[index] ? "" : "rotate-180",
                      "-text--theme-colors-action-ghost-default-fg grow",
                    )}
                    name="fa-chevron-down"
                    size="xs"
                  />
                </div>

                <Text className="-text--theme-colors-text-subtitle-brand font-bold leading-6 text-base">
                  {t("staySection.reservationLabel")} {index + 1}
                </Text>
              </div>
            </Button>

            {reservation.is_added_by_user && (
              <Button
                as="link"
                to={ROUTES.dashboard.reservation(
                  userId,
                  reservation.reservation_id!,
                )}
                variant="ghost-unstyled"
                size="sm"
                className="me-2.5"
              >
                <div className="-text--theme-colors-action-ghost-default-fg">
                  <FontAwesomeIcon name="fa-pen" size="xl" className="p-1" />
                </div>
              </Button>
            )}
          </div>

          {isExpanded[index] && (
            <Stack className="overflow-hidden">
              <ReservationData reservation={reservation} />

              <HR className="my-5" />

              <Stack className="gap-y-5">
                {reservation.rooms?.map((room, i) => {
                  return (
                    <>
                      {i > 0 && (
                        <HR className="hidden md:block !my-0" isDashed />
                      )}

                      <RoomDetails
                        key={i}
                        number={i + 1}
                        reservation={reservation}
                        room={room}
                      />
                    </>
                  );
                })}
              </Stack>
            </Stack>
          )}
        </Stack>
      ))}
    </Stack>
  );
};

const ReservationData = ({
  reservation,
}: {
  reservation: UnGuard<
    UnGuard<GetTripsResponse["approved_trips"]>[number]["reservations"]
  >[number];
}) => {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 -text---theme-colors-text-body gap-y-5">
      <div className="flex flex-col gap-y-[5px] min-w-0">
        <Text className="text-sm font-bold leading-5 -text--theme-colors-text-subtitle-brand">
          {t("staySection.hotelLabel")}
        </Text>
        <Text className="leading-6 line-clamp-4 break-all pe-1">
          {reservation?.name}
        </Text>
      </div>

      <div className="flex flex-col gap-y-[5px] min-w-0">
        <Text className="text-sm font-bold leading-5 -text--theme-colors-text-subtitle-brand">
          {t("staySection.addressLabel")}
        </Text>
        <Text className="leading-6 line-clamp-4 break-all pe-1">
          {reservation?.address}
        </Text>
      </div>

      <div className="flex flex-col gap-y-[5px]">
        <Text className="text-sm font-bold leading-5 -text--theme-colors-text-subtitle-brand">
          {t("staySection.datesLabel")}
        </Text>

        <div className="flex items-center gap-x-10">
          <Text className="leading-6">
            {getFormattedDate(reservation?.start_date)}
          </Text>
          <Text className="leading-6">
            {getFormattedDate(reservation?.end_date)}
          </Text>
        </div>
      </div>

      {!reservation.is_added_by_user && (
        <div className="flex flex-col gap-y-[5px]">
          <Text className="text-sm font-bold leading-5 -text--theme-colors-text-subtitle-brand">
            {t("staySection.hotelStartsLabel")}
          </Text>

          <Text className="leading-6">
            <div className="flex mt-1">
              <Rating
                range={reservation.stars}
                score={reservation.stars ?? 0}
              />
            </div>
          </Text>
        </div>
      )}
    </div>
  );
};

const RoomDetails = ({
  room,
  number,
  reservation,
}: {
  number: number;
  room: UnGuard<
    UnGuard<
      UnGuard<GetTripsResponse["approved_trips"]>[number]["reservations"]
    >[number]["rooms"]
  >[number];
  reservation: UnGuard<
    UnGuard<GetTripsResponse["approved_trips"]>[number]["reservations"]
  >[number];
}) => {
  const { t } = useTranslation();
  return (
    <Stack className="gap-y-5">
      <Text className="-text--theme-colors-text-subtitle-brand font-bold">
        {t("staySection.roomLabel")} {number}
      </Text>

      <Stack className="lg:flex-row gap-x-8">
        <IMG
          className="hidden md:block w-[300px] h-[170px] object-cover rounded-sg"
          alt="hotel-img"
          src={
            reservation.is_added_by_user
              ? RoomUserPlaceholder
              : room.room_images?.[0] || RoomPlaceholder
          }
        />

        <Stack className="-text--theme-colors-text-body gap-y-5">
          {!!room.guests?.length && (
            <div className="flex flex-col w-full gap-y-1.5">
              <Text
                as="span"
                className="-text--theme-colors-text-subtitle-brand text-sm leading-5"
              >
                {t("staySection.passenegersLabel")}
              </Text>

              <Stack className="md:flex-row flex-wrap gap-x-5 gap-y-3">
                {room.guests.map((guest, i) => (
                  <div className="flex gap-x-2.5 items-center min-w-0">
                    <Avatar
                      key={i}
                      src={guest.image || guest.avatar}
                      className="!h-8 !w-8 ring-1 -ring--theme-colors-border-neutral-inverse rounded-full object-cover shrink-0"
                    />
                    <Text className="line-clamp-4 break-all pe-1">
                      {guest.first_name + " " + guest.last_name}
                    </Text>
                  </div>
                ))}
              </Stack>
            </div>
          )}

          <HR className="hidden md:block !my-0" isDashed />

          <div
            className={cn("grid grid-cols-1", { "grid-cols-2": room.sleeps })}
          >
            <div className="flex flex-col gap-y-1.5 min-w-0">
              <Text
                as="span"
                className="-text--theme-colors-text-subtitle-brand text-sm font-bold"
              >
                {t("staySection.roomTypeLabel")}
              </Text>

              <Text className="line-clamp-4 break-all pe-1">
                {room.room_type_name}
              </Text>
            </div>

            {room.sleeps && (
              <div className="flex flex-col gap-y-1.5 min-w-0">
                <Text
                  as="span"
                  className="-text--theme-colors-text-subtitle-brand text-sm font-bold"
                >
                  {t("staySection.sleepsLabel")}
                </Text>

                <Text className="line-clamp-4 break-all pe-1">
                  {room.sleeps}
                </Text>
              </div>
            )}
          </div>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default StaySection;
